import { uniq } from 'lodash';

import { numberWithCommas } from '@common/utils';
import {
  GetMaximumBenefit_payment_event_list_for_display_item_list,
  GetMaximumBenefit_payment_notification_list_item_list,
  PaymentEventDetailList,
  PaymentMethod,
  PaymentNotificationType,
} from '@domains/pdp-common';

import { PaymentEventItem } from '../services/useBenefitService';
import { getPaymentBenefitText } from './getPaymentBenefitText';

function findMaxDiscountItem(eventDetailList: PaymentEventDetailList[], finalPrice: number) {
  return eventDetailList.reduce(
    (maxItem, currentItem) => {
      const isEligible = finalPrice >= currentItem.min_amount;
      const hasHigherDiscount = currentItem.discount_amount > maxItem.discount_amount;

      return isEligible && hasHigherDiscount ? currentItem : maxItem;
    },
    { discount_amount: 0, min_amount: 0 },
  );
}

export const normalizePaymentList = (
  paymentList: {
    paymentEventList: GetMaximumBenefit_payment_event_list_for_display_item_list[];
    finalDiscountPrice: number;
  },
  payment_notification_list: GetMaximumBenefit_payment_notification_list_item_list[],
) => {
  const paymentEventList = paymentList.paymentEventList.reduce((acc: PaymentEventItem[], cur, index) => {
    if (cur.payment_event_detail_list.length > 0) {
      const maxDiscountItem = findMaxDiscountItem(cur.payment_event_detail_list, paymentList.finalDiscountPrice);

      if (maxDiscountItem.discount_amount > 0) {
        acc.push({
          ...cur,
          id: index,
          min_amount: maxDiscountItem.min_amount,
          discount_amount: maxDiscountItem.discount_amount,
        });
      }
    }
    return acc;
  }, []);

  const eventPaymentNotificationList = payment_notification_list.reduce((acc: PaymentEventItem[], cur, index) => {
    if (cur.notification_type === PaymentNotificationType.EVENT && cur.discount_amount && cur.discount_amount > 0) {
      acc.push({
        ...cur,
        id: paymentEventList.length + index,
        min_amount: cur.min_amount ?? 0,
        discount_amount: cur.discount_amount ?? 0,
        payment_method: cur.payment_method,
        payment_method_type: null,
      });
    }
    return acc;
  }, []);

  return paymentEventList.concat(eventPaymentNotificationList);
};

export const filterPaymentListByMinAmount = (paymentList: PaymentEventItem[], price: number): PaymentEventItem[] => {
  return paymentList.filter((item) => price >= item.min_amount);
};

export const calculatePaymentBenefitInfo = (
  language: 'ko' | 'en' | 'ja',
  paymentList: PaymentEventItem[],
  price: number,
) => {
  if (!paymentList.length) {
    return null;
  }
  const discount_amount_list = paymentList.map((item) => item.discount_amount);
  const max_discount = Math.max(...discount_amount_list);
  const max_discount_price = numberWithCommas(price - max_discount);

  const max_payment_event = paymentList.find((item) => item.discount_amount === max_discount);
  const max_card_name = getPaymentBenefitText({
    language,
    payment_method: max_payment_event?.payment_method as PaymentMethod,
    payment_method_type: max_payment_event?.payment_method_type ?? null,
    financial_company_code: max_payment_event?.financial_company_code as string,
    payment_notification_content_list: [],
  });

  const payment_event_card_name_list = paymentList.map((item) =>
    getPaymentBenefitText({
      language,
      payment_method: item.payment_method as PaymentMethod,
      payment_method_type: item.payment_method_type,
      financial_company_code: item.financial_company_code as string,
      payment_notification_content_list: [],
    }).replace('카드', ''),
  );

  const uniq_payment_event_card_name_list = uniq(payment_event_card_name_list).join('/');

  if (discount_amount_list.length === 1) {
    return {
      content: `${max_card_name}로 결제 시 ${max_discount_price}원`,
      card_name: '',
    };
  }

  return {
    content: `결제혜택 최대 적용 시 ${max_discount_price}원`,
    card_name: `(${uniq_payment_event_card_name_list})`,
  };
};
